import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, ExternalLink, Users, ArrowUp, ArrowDown } from "lucide-react";
import { usePartners } from "@/hooks/usePartners";
import { toast } from "sonner";

interface PartnerForm {
  name: string;
  logo_url: string;
  website_url: string;
  description: string;
  order_index: number;
  is_active: boolean;
}

export const PartnersSection = () => {
  const { partners, loading, createPartner, updatePartner, deletePartner, updatePartnerOrder } = usePartners();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<string | null>(null);
  const [formData, setFormData] = useState<PartnerForm>({
    name: "",
    logo_url: "",
    website_url: "",
    description: "",
    order_index: 0,
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      website_url: "",
      description: "",
      order_index: partners.length,
      is_active: true,
    });
    setEditingPartner(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingPartner) {
        await updatePartner(editingPartner, formData);
        toast.success("Գործընկերը հաջողությամբ թարմացվեց");
      } else {
        await createPartner({ ...formData, order_index: partners.length });
        toast.success("Գործընկերը հաջողությամբ ավելացվեց");
      }
      
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Սխալ է տեղի ունեցել");
      console.error('Error saving partner:', error);
    }
  };

  const handleEdit = (partner: any) => {
    setFormData({
      name: partner.name,
      logo_url: partner.logo_url,
      website_url: partner.website_url || "",
      description: partner.description || "",
      order_index: partner.order_index,
      is_active: partner.is_active,
    });
    setEditingPartner(partner.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deletePartner(id);
      toast.success("Գործընկերը հաջողությամբ ջնջվեց");
    } catch (error) {
      toast.error("Սխալ է տեղի ունեցել");
      console.error('Error deleting partner:', error);
    }
  };

  const handleOrderChange = async (id: string, direction: 'up' | 'down') => {
    try {
      await updatePartnerOrder(id, direction);
      toast.success("Կարգը փոփոխվեց");
    } catch (error) {
      toast.error("Սխալ է տեղի ունեցել");
      console.error('Error updating order:', error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Գործընկերների կառավարում
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Գործընկերների կառավարում
          </CardTitle>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Ավելացնել գործընկեր
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingPartner ? "Փոփոխել գործընկերին" : "Ավելացնել նոր գործընկեր"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Անվանում *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="logo_url">Լոգոյի URL *</Label>
                    <Input
                      id="logo_url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="website_url">Կայքի հղում</Label>
                  <Input
                    id="website_url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Նկարագրություն</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Ակտիվ</Label>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Չեղարկել
                  </Button>
                  <Button type="submit">
                    {editingPartner ? "Պահպանել" : "Ավելացնել"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {partners.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Դեռ գործընկերներ չեն ավելացվել
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Լոգո</TableHead>
                <TableHead>Անվանում</TableHead>
                <TableHead>Կարգ</TableHead>
                <TableHead>Կայքի հղում</TableHead>
                <TableHead>Կարգավիճակ</TableHead>
                <TableHead>Գործողություններ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell>
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="w-12 h-12 object-contain"
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{partner.name}</div>
                      {partner.description && (
                        <div className="text-sm text-muted-foreground truncate max-w-xs">
                          {partner.description}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="text-sm">{partner.order_index}</span>
                      <div className="flex flex-col">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOrderChange(partner.id, 'up')}
                          className="h-4 w-4 p-0"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOrderChange(partner.id, 'down')}
                          className="h-4 w-4 p-0"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {partner.website_url ? (
                      <a
                        href={partner.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Այցելել
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={partner.is_active ? "default" : "secondary"}>
                      {partner.is_active ? "Ակտիվ" : "Ոչ ակտիվ"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(partner)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Վստա՞հ եք</AlertDialogTitle>
                            <AlertDialogDescription>
                              Այս գործողությունը հնարավոր չէ հետ կանգնել: Գործընկերը կկջնջվի մշտապես:
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Չեղարկել</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(partner.id)}>
                              Ջնջել
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};